/**
 * Small DOM helpers shared by the overlay and menu primitives.
 *
 * Kept deliberately tiny: everything here is a few lines that would otherwise
 * be copy-pasted into four components and drift.
 */

/**
 * Elements that can receive keyboard focus. `:not([tabindex="-1"])` matters —
 * a focus trap must not cycle through programmatically-focusable-only nodes,
 * or Tab lands on a scroll container and the user thinks focus vanished.
 */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  'summary',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** Focusable descendants of `root`, in document order, excluding hidden ones. */
export function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  const found = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
  return found.filter(isVisible)
}

/**
 * jsdom reports no layout, so `offsetParent` is always null there and a naive
 * visibility test would filter everything out and silently disable the focus
 * trap in tests. Check the properties that jsdom *does* model instead.
 */
export function isVisible(el: HTMLElement): boolean {
  if (el.hasAttribute('hidden')) return false
  if (el.getAttribute('aria-hidden') === 'true') return false
  if (el.closest('[inert]')) return false
  const style = typeof getComputedStyle === 'function' ? getComputedStyle(el) : null
  if (style && (style.display === 'none' || style.visibility === 'hidden')) return false
  return true
}

let idCounter = 0

/**
 * A stable unique id for the aria-* wiring (`aria-labelledby`, `aria-controls`,
 * `aria-activedescendant`). Vue 3.5 ships `useId()`, but it is only available
 * inside a component instance and several helpers here need an id outside one.
 */
export function uid(prefix = 'ui'): string {
  idCounter += 1
  return `${prefix}-${idCounter}`
}

/** Resets the id counter. Test-only; keeps snapshot ids deterministic. */
export function __resetUid(): void {
  idCounter = 0
}

/** True when the user has asked the OS to reduce motion. */
export function prefersReducedMotion(): boolean {
  if (typeof matchMedia !== 'function') return false
  return matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Reads a CSS custom property off an element as a concrete colour string.
 *
 * This is how the charts stay on-palette: uPlot and `<canvas>` take literal
 * colours, so the value is read from the computed style at draw time rather
 * than hardcoded. Change the theme, redraw, get the new colour — with no
 * colour literal anywhere in the component.
 */
export function readToken(el: Element | null, name: string, fallback = ''): string {
  const target = el ?? (typeof document !== 'undefined' ? document.documentElement : null)
  if (!target || typeof getComputedStyle !== 'function') return fallback
  const value = getComputedStyle(target).getPropertyValue(name).trim()
  return value || fallback
}

/** Reads several custom properties in one pass. */
export function readTokens<K extends string>(
  el: Element | null,
  names: readonly K[],
  fallback = '',
): Record<K, string> {
  const out = {} as Record<K, string>
  for (const name of names) out[name] = readToken(el, name, fallback)
  return out
}
